import { useCropper } from '@shared/components/MCropper/useCropper';
import { getCropDataFromSoftCropUsingThumbnail, resolveBrowserImageDimensions } from '@shared/services/crop';
import type { CartPackageImageNodeDisplayModel } from '@shared/models/carts/cartPackageImageNodes';
import type { Cartv2PackageItemDisplayModel } from '@shared/models/carts/cartPackages';
import type { SubjectImageDisplayModel } from '@shared/models/subjects/images';

/**
 * RSPO consumer Step 4 — studio soft-crop DEFAULT framing.
 *
 * The initial image assignment (step 3, `applyIndividualImageToItemNodes` /
 * `applyGroupImageToItemNodes`) sets an image on each node but no `cropDimensions`, so MLivePreview
 * auto-fits each node dead-centre — which looks off when the studio framed the subject off-centre.
 *
 * This applies the studio's existing SOFT CROP as each node's default framing, EXACTLY the way
 * `MLivePreviewModal.handleImageSelection` does on image change: derive the soft-crop pixel rect from
 * the image's thumbnail, then run the shared `softCropImage` cropper to produce order-XML crop
 * dimensions. One framing intent on the source image → a correct crop per node. A node that already
 * carries `cropDimensions` (a per-item override the consumer set in the modal) is left untouched.
 *
 * Heavy + async: each call mounts a hidden `GlobalCropper` (into `#global-croppers`, present app-wide
 * via App.vue), so nodes are processed with `Promise.all`.
 */

const CENTERED_FALLBACK = '50 50 100 100';

/** Apply the studio soft crop to a single node from its assigned subject image. */
export async function applySoftCropToNode(
  node: CartPackageImageNodeDisplayModel,
  image: SubjectImageDisplayModel,
): Promise<CartPackageImageNodeDisplayModel> {
  // Stamp the image's post-EXIF browser dimensions on the node: MLivePreview's -vert/-horiz
  // template decision needs them, and the consumer flow otherwise never sets them (the studio
  // Entry flow does). Memoized — at most 2 distinct images per pass share one <img> load.
  const dims = image.imageThumbnailUrl ? await resolveBrowserImageDimensions(image.imageThumbnailUrl) : null;
  const dimensionFields = dims ? { imageWidth: dims.width, imageHeight: dims.height } : {};

  try {
    const { softCropImage } = useCropper();
    const crop = await getCropDataFromSoftCropUsingThumbnail(image);
    const aspectRatio = crop.width / crop.height;
    const result = await softCropImage(image, aspectRatio, crop);

    if (!result) {
      return { ...node, ...dimensionFields, cropDimensions: CENTERED_FALLBACK, rotation: image.rotation ?? 0 };
    }

    return {
      ...node,
      ...dimensionFields,
      frontendCropDimensions: result.coordinates,
      frontendCropDimensionsStr: JSON.stringify(result.coordinates),
      cropDimensions: result.orderXmlCoordinates,
      rotation: result.image.transforms.rotate ?? 0,
    };
  } catch (error) {
    console.error('Failed to apply soft-crop default to node', node.name, error);
    return { ...node, ...dimensionFields, cropDimensions: CENTERED_FALLBACK, rotation: image.rotation ?? 0 };
  }
}

/**
 * Assign a newly chosen subject image to a node AND frame it with the studio soft crop, mirroring
 * `MLivePreviewModal.handleImageSelection`. Used by the inline item card's "change photo" action so a
 * swapped image lands correctly framed (not dead-centre). Clears any prior crop first so the soft crop
 * is recomputed for the new image.
 */
export async function applyImageAndSoftCropToNode(
  node: CartPackageImageNodeDisplayModel,
  image: SubjectImageDisplayModel,
): Promise<CartPackageImageNodeDisplayModel> {
  const withImage: CartPackageImageNodeDisplayModel = {
    ...node,
    subjectImageId: image.id ?? null,
    groupImageId: image.createdFromGroupImageId ?? null,
    imageThumbnailUrl: image.imageThumbnailUrl ?? null,
    imageUrl: image.imageUrl ?? null,
    cropDimensions: null,
    frontendCropDimensions: null,
    frontendCropDimensionsStr: undefined,
    rotation: image.rotation ?? 0,
  };
  return applySoftCropToNode(withImage, image);
}

/**
 * Apply soft-crop defaults to every assigned, not-yet-cropped image node of one cart item.
 * `imagesById` maps `subjectImageId` → the subject image (both individual portraits and group photos
 * key off `subjectImageId`, which is set for every assigned node).
 */
export async function applySoftCropDefaultsToItem(
  item: Cartv2PackageItemDisplayModel,
  imagesById: Map<number, SubjectImageDisplayModel>,
): Promise<Cartv2PackageItemDisplayModel> {
  const imageNodes = await Promise.all(
    (item.imageNodes ?? []).map(async (node) => {
      // Respect per-item overrides and skip nodes without an assigned image.
      if (node.cropDimensions) return node;
      if (node.subjectImageId == null) return node;
      const image = imagesById.get(node.subjectImageId);
      if (!image) return node;
      return applySoftCropToNode(node, image);
    }),
  );

  return { ...item, imageNodes };
}
